import { segmentDataStructureTypeToDataStructure } from "./segment-data-structures";
import {
  CustomerActivityType,
  SegmentConditionCategory,
} from "./segment-types";

/**
 * What an activity is. The data structures say what you can filter it by, and
 * own its source, so that is not restated here.
 */
export interface CustomerActivityMetadata {
  name: string;
  description: string;
  category: SegmentConditionCategory;
}

export const customerActivityMetadata: Record<
  CustomerActivityType,
  CustomerActivityMetadata
> = {
  [CustomerActivityType.RECEIVED_EMAIL]: {
    name: "Received email",
    description:
      "An email was sent to this person and their inbox received it.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.OPENED_EMAIL]: {
    name: "Opened email",
    description: "An email was sent to this person and they opened it.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.CLICKED_EMAIL]: {
    name: "Clicked email",
    description: "Person clicked a link in the email they received.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.RECEIVED_TEXT]: {
    name: "Received text",
    description: "A text was sent to this person and their device received it.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.CLICKED_TEXT]: {
    name: "Clicked text",
    description: "Person clicked a link in the text they received.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]: {
    name: "Viewed product (Shopify)",
    description: "Person saw a product on your Shopify store.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]: {
    name: "Viewed product (CommentSold)",
    description: "Person saw a product on your CommentSold store.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]: {
    name: "Added product to cart (Shopify)",
    description: "Person added a product to their cart on Shopify.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]: {
    name: "Added product to cart (CommentSold)",
    description: "Person added a product to their cart on CommentSold.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]: {
    name: "Placed order (Shopify)",
    description: "Person placed an order on your Shopify store.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]: {
    name: "Placed order (CommentSold)",
    description: "Person placed an order on your CommentSold store.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]: {
    name: "Started checkout (Shopify)",
    description: "Person made it to the checkout screen on Shopify.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]: {
    name: "Started checkout (CommentSold)",
    description: "Person made it to the checkout screen on CommentSold.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]: {
    name: "Been active on site (Shopify)",
    description: "Person visited your Shopify site.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]: {
    name: "Been active on site (CommentSold)",
    description: "Person visited your CommentSold site.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]: {
    name: "Viewed collection (Shopify)",
    description: "Person viewed a collection on your Shopify store.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.PAYMENT_FAILED_NO_ORDER_CREATED_SHOPIFY]: {
    name: "Payment failed (No order created)",
    description:
      "Person saw a Shopify payment failure before an order was created.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.PAYMENT_FAILED_AFTER_ORDER_CREATED_SHOPIFY]: {
    name: "Payment failed (After order created)",
    description:
      "A Shopify order transaction failed after an order was created.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]: {
    name: "Viewed collection (CommentSold)",
    description: "Person viewed a collection on your CommentSold store.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_BIGCOMMERCE]: {
    name: "Added product to cart (BigCommerce)",
    description: "Person added a product to their cart on BigCommerce.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.CHECKOUT_STARTED_BIGCOMMERCE]: {
    name: "Started checkout (BigCommerce)",
    description: "Person made it to the checkout screen on BigCommerce.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.ORDER_PLACED_BIGCOMMERCE]: {
    name: "Placed order (BigCommerce)",
    description: "Person placed an order on your BigCommerce store.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.RETURN_PROCESSED]: {
    name: "Processed return",
    description: "A return for this person was processed.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]: {
    name: "Subscribed to back-in-stock notifications",
    description:
      "Person subscribed to be notified when a product is back in stock.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]: {
    name: "Triggered back-in-stock notification",
    description:
      "Person received a notification that a product is back in stock.",
    category: SegmentConditionCategory.SHOPPING,
  },
  [CustomerActivityType.BOUNCED_EMAIL]: {
    name: "Bounced email",
    description: "An email sent to this person bounced and was not delivered.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.REPLIED_SMS]: {
    name: "Replied by text",
    description: "Person sent an inbound SMS reply.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.MARKETING_SUBSCRIBED]: {
    name: "Marketing subscribed",
    description: "Person subscribed to email or SMS marketing.",
    category: SegmentConditionCategory.MARKETING,
  },
  [CustomerActivityType.MARKETING_CONFIRMED]: {
    name: "Marketing confirmed",
    description: "Person confirmed their marketing subscription.",
    category: SegmentConditionCategory.MARKETING,
  },
};

export function getCustomerActivity(type: CustomerActivityType) {
  return {
    ...customerActivityMetadata[type],
    source: segmentDataStructureTypeToDataStructure[type].source,
  };
}
